import React, {
  KeyboardEvent,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  HiOutlineArrowUturnLeft,
  HiOutlineCheck,
  HiOutlineCheckCircle,
  HiOutlineLockClosed,
  HiOutlinePaperAirplane,
} from "react-icons/hi2";
import { supportApi } from "../../api/support";
import { ISupportConversation, ISupportMessage } from "../../data-access";
import { FormButton, Spinner } from "../../ui";
import { notify } from "../../util";
import { AttachmentImage } from "./attachment-image";
import { formatMessageTime, getDisplayName } from "./format";

interface IConversationThreadProps {
  conversation: ISupportConversation;
  onRead: (conversationId: string) => void;
  onStatusChange: (conversation: ISupportConversation) => void;
  onReplySent: () => void;
}

const MAX_BODY_LENGTH = 5000;

const baseClass = "support-thread";

// How close to the bottom the agent has to be for a new message to pull the
// thread down.
const NEAR_BOTTOM_THRESHOLD = 64;

const readError = (error: { text?: string; message?: string }, fallback: string): string =>
  error?.text || error?.message || fallback;

/**
 * Folds a response into what is already on screen. Ordered by
 * `(created_at, id)`, the order the backend pages these tables in - the
 * timestamps are second-precision so the ordered UUID breaks the ties.
 */
const mergeMessagesById = (
  previous: ISupportMessage[],
  incoming: ISupportMessage[]
): ISupportMessage[] => {
  const byId = new Map<string, ISupportMessage>();

  previous.forEach((message) => byId.set(message.id, message));
  incoming.forEach((message) => byId.set(message.id, message));

  return Array.from(byId.values()).sort((first, second) => {
    if (first.created_at !== second.created_at) {
      return first.created_at < second.created_at ? -1 : 1;
    }

    return first.id < second.id ? -1 : 1;
  });
};

/**
 * The first message the agent had not read when the thread opened. Opening the
 * thread marks it read, so this is read once off the first response and then
 * held - it marks where the reader came in, not a live state.
 */
const findFirstUnreadId = (messages: ISupportMessage[]): string | null =>
  messages.find(
    (message) => message.sender_type === "user" && !message.read_at
  )?.id ?? null;

export const ConversationThread = ({
  conversation,
  onRead,
  onStatusChange,
  onReplySent,
}: IConversationThreadProps): JSX.Element => {
  const [messages, setMessages] = useState<ISupportMessage[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState<boolean>(false);
  const [reply, setReply] = useState<string>("");
  // Frozen at the moment the thread opened, so the divider stays put while the
  // agent reads and disappears the next time they come back to the thread.
  const [unreadDividerId, setUnreadDividerId] = useState<string | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  const loadedThreadRef = useRef<string>("");
  // The list as it stands right now, so a response that resolves between two
  // renders merges into the current messages rather than a stale closure.
  const messagesRef = useRef<ISupportMessage[]>([]);
  // True while the agent is at the bottom of the thread. Nothing scrolls the
  // pane for them while this is false.
  const isPinnedToBottomRef = useRef<boolean>(true);
  // Distance from the bottom to hold on to after older messages are prepended,
  // so "Load earlier messages" leaves the reader where they were.
  const pendingRestoreRef = useRef<number | null>(null);

  const conversationId = conversation.id;
  const lastMessageAt = conversation.last_message_at;
  const isClosed = conversation.status === "closed";

  const applyMessages = (next: ISupportMessage[]): void => {
    messagesRef.current = next;
    setMessages(next);
  };

  // Reloads on thread switch and whenever the polled list reports a newer
  // message on the open thread, so an answer that lands while support is
  // reading it does not stay invisible.
  useEffect(() => {
    let isActive = true;
    const isNewThread = loadedThreadRef.current !== conversationId;

    if (isNewThread) {
      setIsLoading(true);
      applyMessages([]);
      setReply("");
      setUnreadDividerId(null);
      isPinnedToBottomRef.current = true;
      pendingRestoreRef.current = null;
    }

    supportApi
      .getConversation(conversationId)
      .then((response) => {
        if (!isActive) {
          return;
        }

        loadedThreadRef.current = conversationId;

        // A reload triggered by a new message must not throw away the history
        // the agent paged back to, so the newest window is folded in by id.
        const merged = isNewThread
          ? response.messages
          : mergeMessagesById(messagesRef.current, response.messages);

        applyMessages(merged);

        // Only on the way in. A poll-triggered reload of the same thread must
        // not move a divider the agent is already reading past.
        if (isNewThread) {
          setUnreadDividerId(findFirstUnreadId(merged));
        }

        // has_more describes the window the server just returned, which only
        // describes the whole thread while that window still starts at the
        // oldest message on screen. After paging back it does not, so the
        // value the last "Load earlier messages" wrote stays.
        if (isNewThread || merged[0]?.id === response.messages[0]?.id) {
          setHasMore(response.has_more);
        }

        return supportApi.markRead(conversationId).then(() => {
          if (isActive) {
            onRead(conversationId);
          }
        });
      })
      .catch((error) => {
        if (isActive) {
          notify("error", readError(error, "Failed to load the conversation"));
        }
      })
      .finally(() => {
        if (isActive) {
          setIsLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, lastMessageAt]);

  // isLoadingMore is deliberately not a dependency here. It flips back to false
  // in the same render as the prepended messages, so gating on it never fired
  // and every "Load earlier messages" click jumped straight back to the newest
  // message. The restore distance and the pinned flag are refs for that reason.
  useLayoutEffect(() => {
    const node = scrollRef.current;

    if (!node) {
      return;
    }

    if (pendingRestoreRef.current !== null) {
      node.scrollTop = node.scrollHeight - pendingRestoreRef.current;
      pendingRestoreRef.current = null;

      return;
    }

    if (!isPinnedToBottomRef.current) {
      return;
    }

    node.scrollTop = node.scrollHeight;
  }, [messages]);

  const handleThreadScroll = (): void => {
    const node = scrollRef.current;

    if (!node || pendingRestoreRef.current !== null) {
      return;
    }

    isPinnedToBottomRef.current =
      node.scrollHeight - node.scrollTop - node.clientHeight <=
      NEAR_BOTTOM_THRESHOLD;
  };

  const handleLoadEarlier = (): void => {
    const node = scrollRef.current;

    if (!messagesRef.current.length || isLoadingMore) {
      return;
    }

    // Held as a distance from the bottom, which survives the thread growing at
    // the top when the older page is prepended.
    const restoreDistance = node ? node.scrollHeight - node.scrollTop : 0;

    isPinnedToBottomRef.current = false;
    setIsLoadingMore(true);

    supportApi
      .getConversation(conversationId, messagesRef.current[0].id)
      .then((response) => {
        pendingRestoreRef.current = restoreDistance;
        applyMessages(
          mergeMessagesById(messagesRef.current, response.messages)
        );
        setHasMore(response.has_more);
      })
      .catch((error) => {
        notify("error", readError(error, "Failed to load earlier messages"));
      })
      .finally(() => setIsLoadingMore(false));
  };

  const handleSend = (): void => {
    const body = reply.trim();

    if (!body || isSending) {
      return;
    }

    setIsSending(true);

    supportApi
      .sendMessage(conversationId, body)
      .then((message) => {
        // The agent just sent this, so the pane follows it down.
        isPinnedToBottomRef.current = true;
        applyMessages(mergeMessagesById(messagesRef.current, [message]));
        setReply("");
        onReplySent();
      })
      .catch((error) => {
        notify("error", readError(error, "Failed to send the reply"));
      })
      .finally(() => setIsSending(false));
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      handleSend();
    }
  };

  const handleToggleStatus = (): void => {
    setIsUpdatingStatus(true);

    supportApi
      .setStatus(conversationId, isClosed ? "open" : "closed")
      .then((updated) => {
        onStatusChange(updated);
        notify("success", isClosed ? "Conversation reopened" : "Conversation closed");
      })
      .catch((error) => {
        notify("error", readError(error, "Failed to update the conversation"));
      })
      .finally(() => setIsUpdatingStatus(false));
  };

  const { user } = conversation;

  // Read receipts sit under the agent's own last message only. One per thread -
  // a "Read" under every bubble is noise.
  const lastStaffMessage = [...messages]
    .reverse()
    .find((message) => message.sender_type === "staff");

  return (
    <div className={baseClass}>
      <div className={`${baseClass}__header`}>
        <div className={`${baseClass}__who`}>
          <span className={`${baseClass}__name`}>
            {getDisplayName(user.full_name, user.email)}
          </span>
          <span className={`${baseClass}__email`}>{user.email}</span>
        </div>
        <div className={`${baseClass}__actions`}>
          <span className={`${baseClass}__status ${baseClass}__status--${conversation.status}`}>
            {isClosed ? "Closed" : "Open"}
          </span>
          <FormButton
            theme="grey"
            disabled={isUpdatingStatus}
            icon={
              isClosed ? (
                <HiOutlineArrowUturnLeft size={16} />
              ) : (
                <HiOutlineLockClosed size={16} />
              )
            }
            onClick={handleToggleStatus}
          >
            {isClosed ? "Reopen" : "Close"}
          </FormButton>
        </div>
      </div>

      <div
        className={`${baseClass}__messages`}
        ref={scrollRef}
        onScroll={handleThreadScroll}
      >
        {isLoading && <Spinner />}

        {!isLoading && hasMore && (
          <div className={`${baseClass}__load-more`}>
            <FormButton
              theme="grey"
              disabled={isLoadingMore}
              onClick={handleLoadEarlier}
            >
              {isLoadingMore ? "Loading" : "Load earlier messages"}
            </FormButton>
          </div>
        )}

        {!isLoading && messages.length === 0 && (
          <div className={`${baseClass}__empty`}>No messages in this conversation yet</div>
        )}

        {!isLoading &&
          messages.map((message) => (
            <React.Fragment key={message.id}>
              {message.id === unreadDividerId && (
                <div className={`${baseClass}__new-divider`}>
                  <span>New messages</span>
                </div>
              )}
              <div
                className={`${baseClass}__message ${baseClass}__message--${message.sender_type}`}
              >
                <div className={`${baseClass}__bubble`}>
                  {message.body && (
                    <div className={`${baseClass}__text`}>{message.body}</div>
                  )}
                  {message.attachments.map((attachment) => (
                    <AttachmentImage key={attachment.id} attachment={attachment} />
                  ))}
                </div>
                <div className={`${baseClass}__meta`}>
                  {message.sender_name || (message.sender_type === "staff" ? "Support" : "User")}
                  {" · "}
                  {formatMessageTime(message.created_at)}
                </div>
                {message.id === lastStaffMessage?.id && (
                  <div className={`${baseClass}__receipt`}>
                    {message.read_at ? (
                      <>
                        <HiOutlineCheckCircle size={12} />
                        Read
                      </>
                    ) : (
                      <>
                        <HiOutlineCheck size={12} />
                        Sent
                      </>
                    )}
                  </div>
                )}
              </div>
            </React.Fragment>
          ))}
      </div>

      <div className={`${baseClass}__reply`}>
        <textarea
          value={reply}
          maxLength={MAX_BODY_LENGTH}
          placeholder={
            isClosed
              ? "This conversation is closed. Reopen it to keep the thread going."
              : "Write a reply. Cmd or Ctrl + Enter sends it."
          }
          onChange={(event) => setReply(event.target.value)}
          onKeyDown={handleKeyDown}
        />
        <div className={`${baseClass}__reply-footer`}>
          <span className={`${baseClass}__counter`}>
            {reply.length} / {MAX_BODY_LENGTH}
          </span>
          <FormButton
            disabled={isSending || !reply.trim()}
            icon={<HiOutlinePaperAirplane size={16} />}
            onClick={handleSend}
          >
            {isSending ? "Sending" : "Send reply"}
          </FormButton>
        </div>
      </div>
    </div>
  );
};
